import React, { useState, useEffect, useMemo } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Gift, CheckCircle, Sparkles, Wrench } from 'lucide-react'
import { useTranslation } from 'react-i18next'

// Import changelogs for each language
import changelogEn from '../changelog/CHANGELOG.en.md?raw'
import changelogCs from '../changelog/CHANGELOG.cs.md?raw'

// Import version from package.json
import { version as APP_VERSION } from '../../package.json'
export { APP_VERSION }

const changelogs: Record<string, string> = {
  en: changelogEn,
  cs: changelogCs,
}

const STORAGE_KEY = 'urkla_seen_version'

interface ChangelogItem {
  text: string
  children: ChangelogItem[]
}

interface ChangelogSection {
  title: string
  items: ChangelogItem[]
}

interface VersionEntry {
  version: string
  sections: ChangelogSection[]
}

// Parse markdown changelog into structured data
const parseChangelog = (markdown: string): VersionEntry[] => {
  const versions: VersionEntry[] = []
  const lines = markdown.split('\n')

  let currentVersion: VersionEntry | null = null
  let currentSection: ChangelogSection | null = null

  for (const line of lines) {
    // Version header (## 1.0.0)
    const versionMatch = line.match(/^## (\d+\.\d+\.\d+)/)
    if (versionMatch) {
      if (currentVersion) {
        if (currentSection) {
          currentVersion.sections.push(currentSection)
        }
        versions.push(currentVersion)
      }
      currentVersion = { version: versionMatch[1], sections: [] }
      currentSection = null
      continue
    }

    // Section header (### New Features)
    const sectionMatch = line.match(/^### (.+)/)
    if (sectionMatch && currentVersion) {
      if (currentSection) {
        currentVersion.sections.push(currentSection)
      }
      currentSection = { title: sectionMatch[1], items: [] }
      continue
    }

    // List item, top level ("- Something") or indented ("  - Something")
    const itemMatch = line.match(/^(\s*)- (.+)/)
    if (itemMatch && currentSection) {
      const item: ChangelogItem = { text: itemMatch[2], children: [] }
      const parent = itemMatch[1].length > 0 ? currentSection.items[currentSection.items.length - 1] : null
      if (parent) parent.children.push(item)
      else currentSection.items.push(item)
    }
  }

  // Push last entries
  if (currentVersion) {
    if (currentSection) {
      currentVersion.sections.push(currentSection)
    }
    versions.push(currentVersion)
  }

  return versions
}

// Get seen version from localStorage
const getSeenVersion = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

// Set seen version in localStorage
const setSeenVersion = (version: string): void => {
  try {
    localStorage.setItem(STORAGE_KEY, version)
  } catch {
    // Ignore storage errors
  }
}

// Check if we should auto-show the modal
export const shouldShowWhatsNew = (): boolean => {
  const seenVersion = getSeenVersion()
  return seenVersion !== APP_VERSION
}

interface WhatsNewModalProps {
  isOpen: boolean
  onClose: () => void
  showAllVersions?: boolean
}

// Minimal inline markdown: **bold** and `code`, everything else stays literal.
const renderInline = (text: string): React.ReactNode[] =>
  text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className="font-semibold text-foreground">
          {part.slice(2, -2)}
        </strong>
      )
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code key={i} className="rounded bg-muted px-1 py-0.5 text-xs">
          {part.slice(1, -1)}
        </code>
      )
    }
    return <React.Fragment key={i}>{part}</React.Fragment>
  })

const ChangelogItems: React.FC<{ items: ChangelogItem[]; nested?: boolean }> = ({ items, nested }) => (
  <ul className={nested ? 'mt-1.5 space-y-1' : 'space-y-1.5'}>
    {items.map((item, index) => (
      <li key={index} className="text-sm leading-relaxed">
        <span className="flex gap-2">
          <span aria-hidden className={nested ? 'text-muted-foreground/50' : 'text-muted-foreground'}>
            {nested ? '–' : '•'}
          </span>
          <span className={nested ? 'text-muted-foreground' : ''}>{renderInline(item.text)}</span>
        </span>
        {item.children.length > 0 && (
          <div className="pl-4">
            <ChangelogItems items={item.children} nested />
          </div>
        )}
      </li>
    ))}
  </ul>
)

const getSectionIcon = (title: string) => {
  const lower = title.toLowerCase()
  if (lower.includes('feature') || lower.includes('funkce')) return Sparkles
  if (lower.includes('fix') || lower.includes('oprav')) return Wrench
  return CheckCircle
}

const getSectionColorClass = (title: string) => {
  const lower = title.toLowerCase()
  if (lower.includes('feature') || lower.includes('funkce')) return 'text-green-600'
  if (lower.includes('fix') || lower.includes('oprav')) return 'text-amber-600'
  return 'text-blue-600'
}

const WhatsNewModal: React.FC<WhatsNewModalProps> = ({
  isOpen,
  onClose,
  showAllVersions = false,
}) => {
  const { t, i18n } = useTranslation()
  const [showAll, setShowAll] = useState(showAllVersions)

  // Get changelog for current language (fallback to English)
  const changelogRaw = changelogs[i18n.language] || changelogs.en
  const changelog = useMemo(() => parseChangelog(changelogRaw), [changelogRaw])
  const latestVersion = changelog[0]
  const displayVersions = showAll ? changelog : changelog.slice(0, 1)

  // Mark as seen when modal opens
  useEffect(() => {
    if (isOpen) {
      setSeenVersion(APP_VERSION)
    }
  }, [isOpen])

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose() }}>
      {/* flex column + a single scroll container: the dialog itself must not
          scroll, otherwise the wheel moves the wrong element and the footer
          drifts out of reach. */}
      <DialogContent className="flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl">
        <DialogHeader className="shrink-0 border-b px-6 py-4">
          <DialogTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-primary" />
            {t('WhatsNew_title')}
            {latestVersion && (
              <Badge variant="secondary" className="text-xs font-normal">
                v{latestVersion.version}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-6 py-5">
          {displayVersions.map((entry, versionIndex) => (
            <div key={entry.version} className={versionIndex > 0 ? 'border-t pt-6' : ''}>
              {showAll && (
                <div className="mb-3">
                  <Badge variant="outline" className="text-xs">v{entry.version}</Badge>
                </div>
              )}

              <div className="space-y-5">
                {entry.sections.map((section, sectionIndex) => {
                  const IconComponent = getSectionIcon(section.title)
                  return (
                    <div key={sectionIndex}>
                      <h6 className="mb-2 flex items-center gap-2 text-sm font-semibold">
                        <IconComponent className={`h-4 w-4 ${getSectionColorClass(section.title)}`} />
                        {section.title}
                      </h6>
                      <ChangelogItems items={section.items} />
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="shrink-0 items-center justify-between gap-2 border-t px-6 py-4 sm:justify-between sm:space-x-0">
          {changelog.length > 1 ? (
            <Button variant="ghost" size="sm" onClick={() => setShowAll((prev) => !prev)}>
              {showAll ? t('WhatsNew_show_latest_only') : t('WhatsNew_show_all_versions')}
            </Button>
          ) : (
            <span />
          )}
          {/* Radix focuses the first tabbable element otherwise, which put the
              ring on the secondary toggle instead of the primary action. */}
          <Button autoFocus onClick={onClose}>
            {t('WhatsNew_got_it')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default WhatsNewModal
